import { Link } from 'react-router-dom';
import { CONFIG } from '../../constants/config';
import { PRICE, BILLING_NOTE } from '../../constants/pricing';

const FEATURES = [
  { icon: '📍', title: 'Real-time GPS Tracking', desc: 'Watch patroller patrols live on a map. Every position update streams directly to your dashboard.' },
  { icon: '🚨', title: 'Instant Alarm Alerts', desc: 'Patrollers press a single button to raise an alarm. Portal displays full-screen alert and emails all contacts immediately.' },
  { icon: '📋', title: 'Patrol History & Replay', desc: 'Every patrol is stored. Replay any route with our animated timeline to verify coverage.' },
  { icon: '📵', title: 'Offline Resilient', desc: "Patrollers continue patrolling without signal. Track points sync automatically when connectivity returns." },
  { icon: '👥', title: 'Multi-Role Access', desc: 'Admin, Control Room Operator, and Trustee roles with appropriate visibility and permissions.' },
  { icon: '🔒', title: 'Site-Isolated', desc: 'Full multi-tenant isolation. Your patrollers and patrol data are never visible to other sites.' },
  { icon: '💳', title: 'Simple Pricing', desc: `${PRICE} per month per site, covering up to 10 patrollers. No setup fees, no per-device licences, no long-term contract.${BILLING_NOTE}` },
  // Said plainly and up front. Someone who pays and only then discovers their
  // patrollers cannot install the app is a refund and a bad first impression;
  // the honest version costs one card. The distinction matters commercially:
  // only the PATROLLER handsets need Android, and the buyer — an estate
  // manager who may well be on an iPhone — is not affected at all.
  { icon: '📱', title: 'Android Patroller App', desc: 'Patrollers use any modern Android phone. The control room portal is a website, so managers and trustees can use it on iPhone, iPad, Mac or Windows. An iOS patroller app is in development.' },
];


/**
 * Self-hosted rather than a YouTube/Vimeo embed. Three reasons: no third-party
 * cookies or tracking on a page that has to be POPIA-defensible, no branding
 * or "up next" videos from someone else on our shop window, and the file is
 * ours to replace without touching an account. Cost is bandwidth, which is
 * why each one is preload="none" behind a poster — nothing downloads until a
 * visitor actually presses play.
 *
 * nginx serves them from /opt/patrolit/video/, deliberately OUTSIDE the
 * portal's dist/ — a portal deploy wipes dist/, which would delete the videos
 * on every release. Publishing one is an scp; nothing here rebuilds.
 *
 * Off until the files exist. Two players showing a broken poster on the shop
 * window is worse than no section at all, so this stays false until both
 * videos are uploaded — see portal/public/video/README.md.
 */
const SHOW_VIDEOS = false;

const VIDEOS = [
  {
    id: 'portal',
    title: 'The control room view',
    desc: 'Live map, patroller breadcrumbs, alarm and incident pins, and the monthly report a committee actually reads.',
    src: '/video/patrolit-portal.mp4',
    poster: '/video/patrolit-portal.jpg',
  },
  {
    id: 'app',
    title: 'The patroller app',
    desc: 'Signing in, starting a patrol, dropping an incident pin with a note, and raising an alarm.',
    src: '/video/patrolit-app.mp4',
    poster: '/video/patrolit-app.jpg',
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="flex items-center justify-between px-8 py-5 border-b">
        <span className="text-xl font-bold text-gray-900">PatrolIt</span>
        <div className="flex items-center gap-4">
          {/* Demo credentials are public by design — the demo site is
              disposable and demo_restore.sh puts it back. */}
          <Link to="/login?demo=1" className="text-sm text-gray-600 hover:text-gray-900">Try the demo</Link>
          <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900">Sign in</Link>
          <a
            href={`mailto:${CONFIG.CONTACT_EMAIL}?subject=${encodeURIComponent('PatrolIt enquiry')}`}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Email us
          </a>
          <Link to="/register" className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium hover:bg-brand-700 transition-colors">
            Sign up
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="text-center px-8 py-24 bg-gradient-to-b from-brand-50 to-white">
        <h1 className="text-5xl font-extrabold text-gray-900 leading-tight max-w-3xl mx-auto">
          Patrol tracking for residential estates, neighbourhood patrols and businesses
        </h1>
        <p className="mt-5 text-xl text-gray-600 max-w-xl mx-auto">
          Real-time GPS tracking, instant alarm notifications, and full patrol audit trails — all in one platform.
        </p>
      </section>

      {/* See it working */}
      {SHOW_VIDEOS && (
      <section id="demo" className="px-8 py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-3">See it working</h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Two short videos — one from the control room, one from a patroller's phone.
            Or skip them and{' '}
            <Link to="/login?demo=1" className="text-brand-600 font-medium hover:underline">
              open the live demo
            </Link>{' '}
            yourself.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {VIDEOS.map((v) => (
              <div key={v.id}>
                <div className="rounded-2xl overflow-hidden border border-gray-200 bg-black shadow-sm">
                  <video
                    className="w-full aspect-video"
                    controls
                    preload="none"
                    poster={v.poster}
                    playsInline
                  >
                    <source src={v.src} type="video/mp4" />
                    Your browser cannot play this video.{' '}
                    <a href={v.src}>Download it instead.</a>
                  </video>
                </div>
                <h3 className="font-semibold text-gray-900 mt-4">{v.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed mt-1">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Features */}
      <section id="features" className="px-8 py-20 max-w-5xl mx-auto">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Everything your site needs</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {FEATURES.map((f) => (
            <div key={f.title} className="p-6 rounded-2xl border border-gray-100 hover:border-brand-200 transition-colors">
              <div className="text-3xl mb-3">{f.icon}</div>
              <h3 className="font-semibold text-gray-900 mb-2">{f.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing — hidden until billing is configured
      <section className="px-8 py-20 bg-gray-50"> ... </section>
      */}

      {/* Footer */}
      <footer className="text-center px-8 py-8 text-sm text-gray-400 border-t">
        © {new Date().getFullYear()} PatrolIt. All rights reserved.
        {' · '}
        <Link to="/privacy" className="hover:text-gray-600 hover:underline">Privacy Policy</Link>
        {' · '}
        <Link to="/terms" className="hover:text-gray-600 hover:underline">Terms of Service</Link>
      </footer>
    </div>
  );
}
